// src/utils/notification/toastAdapter.js
import Vue from 'vue'
import i18n from '@/libs/i18n'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

/**
 * Safe i18n translate:
 * - if key exists -> translated
 * - else return raw string
 */
function tSafe(keyOrText, params) {
  if (!keyOrText) return ''
  const s = String(keyOrText)
  try {
    if (i18n?.te && i18n.te(s)) return i18n.t(s, params)
  } catch (_) {}
  return s
}

function getToast() {
  return Vue?.prototype?.$toast || null
}

function isVueProxyLike(x) {
  return !!(x && typeof x === 'object' && (x.$toast || x.$t || x._isVue))
}

function isPlainObject(x) {
  return !!x && typeof x === 'object' && !Array.isArray(x)
}

/** Extract axios/response validation errors object */
function getApiErrors(input) {
  if (input?.response?.data?.errors) return input.response.data.errors // axios error
  if (input?.data?.errors) return input.data.errors // axios success payload
  if (input?.errors) return input.errors // raw payload
  return null
}

function hasErrorsShape(input) {
  const errs = getApiErrors(input)
  return !!(errs && typeof errs === 'object' && Object.keys(errs).length)
}

function firstFieldAndMessage(errorsObj) {
  if (!errorsObj || typeof errorsObj !== 'object') return { field: '', msg: '' }
  const field = Object.keys(errorsObj)[0] || ''
  const arr = field ? errorsObj[field] : null
  const msg = Array.isArray(arr) && arr[0] ? String(arr[0]) : ''
  return { field, msg }
}

/**
 * Translate a FULL i18n key (e.g. "errors.location.service_request_policy_invalid")
 * If missing -> return fallbackText
 */
function tFullKeyOrFallback(fullKey, fallbackText) {
  if (!fullKey) return fallbackText
  const translated = i18n.t(fullKey)
  return translated === fullKey ? fallbackText : translated
}

function extractStatus(input) {
  // Axios error
  if (input?.response?.status) return input.response.status

  // Axios success
  if (input?.status) return input.status

  // Custom: { status: 'success'|'error' }
  if (typeof input?.status === 'string') return input.status

  return null
}

function statusToVariant(status) {
  if (status === 'success') return 'success'
  if (status === 'error') return 'danger'

  const n = Number(status)
  if (Number.isFinite(n)) {
    if (n >= 200 && n < 300) return 'success'
    if (n >= 400) return 'danger'
  }
  return 'warning'
}

function variantToIcon(variant) {
  if (variant === 'success') return 'BellIcon'
  if (variant === 'danger') return 'AlertOctagonIcon'
  return 'AlertCircleIcon'
}

/**
 * Extracts a human-readable message from:
 * - Axios error (error.response.data)
 * - Laravel validation error: { message, errors: { field: [..] } }
 * - Your custom { status, statusText, message }
 * - Plain string
 *
 * Supports translating validation errors WITHOUT using English sentences as keys:
 * Pass:
 *  - extra.errorPrefix: e.g. "errors.location" (default: "errors")
 *  - extra.errorFieldKeyMap: { service_request_policy: "service_request_policy_invalid" }
 * Then adapter will translate: `${errorPrefix}.${mappedKey}`
 *
 * If mapping missing or translation missing -> falls back to raw API message.
 */
function extractMessage(input, fallbackKey, extra = {}) {
  const fallback = tSafe(fallbackKey || 'Notification')

  // plain string
  if (typeof input === 'string') return tSafe(input) || fallback

  // 1) Validation errors first
  const rawErrors = getApiErrors(input)
  if (rawErrors) {
    const { field, msg } = firstFieldAndMessage(rawErrors)

    const prefix = extra?.errorPrefix || 'errors'
    const map = extra?.errorFieldKeyMap || {}

    // 1a) Field->key mapping (recommended, avoids English keys)
    if (field && map[field]) {
      const fullKey = `${prefix}.${map[field]}`
      return tFullKeyOrFallback(fullKey, msg || fallback)
    }

    // 1b) No mapping -> show raw first error message
    if (msg) return msg
  }

  // 2) Axios error message
  const ax = input?.response?.data
  if (ax) {
    if (ax.message) return String(ax.message)
    if (ax.statusText) return String(ax.statusText)
  }

  // 3) Non-axios payload message
  const data = input?.data
  if (data) {
    if (data.message) return String(data.message)
    if (data.statusText) return String(data.statusText)
  }

  // 4) Direct object message
  if (input?.message) return String(input.message)

  // legacy { statusText } pattern
  if (input?.statusText) return tSafe(input.statusText) || fallback

  return fallback
}

/**
 * Normalizes legacy and new call signatures.
 *
 * Supports:
 *  - showToastSafe(input, elementKey, subjectKey?, timeout?, extra?)
 *  - showToastSafe(proxy, input, elementKey, subjectKey?, timeout?, extra?)  <-- old style
 *  - showToastSafe(input, optionsObject)
 *  - showToastSafe(proxy, input, optionsObject)
 *
 * optionsObject:
 *  {
 *    element: string,
 *    subject: string,
 *    title: string,            // alias of subject
 *    text: string,             // absolute override text (always wins)
 *    successText: string,
 *    errorText: string,
 *    warningText: string,
 *    timeout: number,
 *    fallbackKey: string,
 *    errorPrefix: string,      // e.g. "errors.location"
 *    errorFieldKeyMap: object  // e.g. { service_request_policy: "service_request_policy_invalid" }
 *  }
 */
function normalizeArgs(argsLike) {
  const a = Array.from(argsLike)

  // Old calls passed proxy first; detect and drop it.
  if (a.length >= 2 && isVueProxyLike(a[0])) a.shift()

  const input = a[0]

  // New signature: showToastSafe(input, { ... })
  if (isPlainObject(a[1])) {
    const opt = a[1] || {}
    return {
      input,
      elementKey: opt.element || 'Notification',
      subjectKey: opt.subject || opt.title || 'Notification',
      timeout: typeof opt.timeout === 'number' ? opt.timeout : 4000,
      extra: opt,
    }
  }

  // Legacy signature: showToastSafe(input, elementKey, subjectKey?, timeout?, extra?)
  const elementKey = a[1] || 'Notification'
  const subjectKey = a[2] || 'Notification'

  let timeout = a[3]

  // allow showToastSafe(input, element, subject, extra) (timeout omitted)
  if (isPlainObject(timeout)) {
    return {
      input, elementKey, subjectKey, timeout: 4000, extra: timeout,
    }
  }

  if (typeof timeout === 'string') timeout = undefined
  if (timeout == null) timeout = 4000

  const extra = (a[4] && typeof a[4] === 'object') ? a[4] : {}

  return {
    input, elementKey, subjectKey, timeout, extra,
  }
}

function pickCustomText(extra, variant) {
  if (!extra) return ''
  if (extra.text) return tSafe(extra.text)
  if (variant === 'success' && extra.successText) return tSafe(extra.successText)
  if (variant === 'danger' && extra.errorText) return tSafe(extra.errorText)
  if (variant === 'warning' && extra.warningText) return tSafe(extra.warningText)
  return ''
}

export function showToastSafe(/* ...args */) {
  const toast = getToast()
  if (!toast) return

  const {
    input, elementKey, subjectKey, timeout, extra,
  } = normalizeArgs(arguments)

  const elementText = tSafe(elementKey) || tSafe('Notification')
  const subjectText = tSafe(subjectKey) || tSafe('Notification')

  const status = extractStatus(input)
  let variant = statusToVariant(status)

  // If status missing but validation errors exist, treat as danger (better UX)
  if (!status && hasErrorsShape(input)) variant = 'danger'

  const icon = variantToIcon(variant)

  const extractedMsg = extractMessage(input, extra?.fallbackKey, extra)
  const customText = pickCustomText(extra, variant)

  let text = customText || ''

  if (!text) {
    if (variant === 'success') {
      const isFallbackish = !extractedMsg
                || extractedMsg === tSafe('Notification')
                || extractedMsg === tSafe(extra?.fallbackKey || 'Notification')

      text = !isFallbackish
        ? extractedMsg
        : `${elementText} - ${tSafe('common.added')}`
    } else {
      text = extractedMsg
    }
  }

  toast(
    {
      component: ToastificationContent,
      props: {
        title: subjectText,
        icon,
        text,
        variant,
      },
    },
    { timeout },
  )
}
